<template>
  <section id="flights">
    <h3>Flights</h3>
    <Route v-if="!isSavedRoute" v-bind="$attrs" v-model:flights="flights" v-model:is-saved-route="isSavedRoute" v-model:leg-index="legIndex" :is-premium="isPremium" @error="$emit('error', $event)" />
    <Options v-if="isSavedRoute && !isSavedFlights && isSelfBook" v-bind="$attrs" v-model:flights="flights" v-model:is-saved-flights="isSavedFlights" v-model:is-saved-route="isSavedRoute" v-model:leg-index="legIndex" :organization-id="organizationId" :cache-bust="cacheBust" @error="$emit('error', $event)" @scroll="$emit('scroll', $event)" @update:is-self-book="$emit('update:isSelfBook', $event)" />
    <SummaryAssist v-if="isSavedRoute && !isSelfBook" v-bind="$attrs" v-model:flights-other="flights.other" v-model:is-saved-route="isSavedRoute" :flights="flights" @complete="complete" />
    <SummaryBook v-if="isSavedRoute && isSavedFlights && isSelfBook" v-bind="$attrs" v-model:flights="flights" v-model:is-saved-flights="isSavedFlights" v-model:is-saved-route="isSavedRoute" v-model:leg-index="legIndex" @complete="complete" @scroll="$emit('scroll', $event)" />
  </section>
</template>

<script setup>
import { ref, computed } from "vue";
import Route from "./TripNewBookFlightsRoute.vue";
import Options from "./TripNewBookFlightsOptions.vue";
import SummaryAssist from "./TripNewBookFlightsSummaryAssist.vue";
import SummaryBook from "./TripNewBookFlightsSummaryBook.vue";
import DateTime from "../utils/datetime";
import { formatPrice } from "../utils/money";

// Extracting necessary functions from DateTime utility
const { utilDateFlightfoxShort, utilDateISO8601 } = DateTime();

// Props
const props = defineProps({
  isSelfBook: Boolean,
  isPremium: Boolean,
  organizationId: [String, Number],
});

// Emits
const emit = defineEmits(["error", "scroll", "update:flights", "update:sectionsFlightsDateStart", "update:sectionsFlightsDateEnd", "update:sectionsFlightsDestination", "update:sectionsFlightsDescription", "update:sectionsFlightsIsComplete", "update:sectionsFlightsFlights", "update:isSelfBook"]);

// State
const flights = ref({
  cabin: props.isPremium ? "business-first" : "economy",
  other: null,
  pax: 1,
  route: [
    { key: 1, depart: "", from: {}, ideal: [], to: {} },
    { key: 2, depart: "", from: {}, ideal: [], to: {} },
  ],
  way: "roundtrip",
});
const isSavedFlights = ref(false);
const isSavedRoute = ref(false);
const legIndex = ref(0);
const cacheBust = ref(0);

// Reset to flight search results, preserving the route. Triggers a fresh cache-busted search.
const resetToSearch = () => {
  isSavedFlights.value = false;
  legIndex.value = 0;
  cacheBust.value++;
};

defineExpose({ resetToSearch });

// Computed properties
const flightsDateStart = computed(() => {
  return utilDateISO8601(flights.value.route[0].depart);
});

const flightsDateEnd = computed(() => {
  return utilDateISO8601(flights.value.route[flights.value.route.length - 1].depart);
});

const flightsDescription = computed(() => {
  let desc = "\r\n\r\n***\r\n\r\nFLIGHTS\r\n";
  desc += "Pax: " + flights.value.pax;
  desc += "\r\nCabin: " + flights.value.cabin;
  desc += "\r\n\r\nRoute:\r\n";
  desc += flightsRouteToText();

  if (props.isSelfBook) {
    desc += "\r\n\r\nIdeal:\r\n";
    desc += flightsIdealToText();
  } else {
    desc += "\r\n\r\n" + flights.value.other;
  }

  return desc;
});

const flightsDestination = computed(() => {
  const origin = flights.value.route[0].from.place;
  const destArray = [];

  flights.value.route.forEach(leg => {
    if (origin != leg.to.place) {
      destArray.push(leg.to.place);
    }
  });

  if (destArray.length == 1) {
    return destArray[0];
  }

  if (destArray.length > 3) {
    return "Multiple Cities";
  }

  const destLast = destArray.pop();
  return destArray.join(", ") + " & " + destLast;
});

const flightsFlights = computed(() => {
  return flights.value.route.map(leg => leg.ideal);
});

const flightsIdealToText = () => {
  const flightsArr = [];
  const price = flights.value.route[flights.value.route.length - 1].ideal.price_selected;
  if (!price) return "";
  const bagInfo = price.is_bag_data ? "Checked: " + price.bag_checked_count + " Carry-on: " + price.bag_carry_on_count : "No baggage info";

  flightsArr.push(priceLabel(price) + "\n");
  flightsArr.push(price.fare + " (" + bagInfo + ")\n");
  flights.value.route.forEach(leg => {
    if ("legs" in leg.ideal) {
      leg.ideal.legs.forEach(flightLeg => {
        flightsArr.push(utilDateFlightfoxShort(flightLeg.time_depart) + " " + flightLeg.airline_iata + flightLeg.number + "\r\n");
      });
    }
  });
  return flightsArr.join("").trim();
};

const flightsRouteToText = () => {
  return flights.value.route
    .map(data => {
      if (data.depart) {
        const day = new Intl.DateTimeFormat("en", { day: "2-digit" }).format(data.depart);
        const month = new Intl.DateTimeFormat("en", { month: "short" }).format(data.depart);
        if (!data.depart) return "";
        return `${day}${month} ${data.from?.iata?.toUpperCase() || ""}-${data.to?.iata?.toUpperCase() || ""}\r\n`;
      }
      return "";
    })
    .join("")
    .trim();
};

// Methods
const complete = () => {
  if (props.isSelfBook) {
    emit("update:sectionsFlightsDateStart", flightsDateStart.value);
    emit("update:sectionsFlightsDateEnd", flightsDateEnd.value);
    emit("update:sectionsFlightsDestination", flightsDestination.value);
    emit("update:sectionsFlightsFlights", flightsFlights.value);
  }

  emit("update:flights", flights.value);
  emit("update:sectionsFlightsDescription", flightsDescription.value);
  emit("update:sectionsFlightsIsComplete", true);
  emit("scroll");
};

// p.amount is canonical USD; formatPrice consults the org currency captured at search time.
const priceLabel = p => formatPrice(p.amount);
</script>
